import type { UseControllerProps } from 'react-hook-form'

import React from 'react'
import { CircleHelpIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

import InlineEditText from '../InlineEditText'

export default function RHFInlineEditText({
	name,
	label,
	hint,
	description,
	defaultValue,
	shouldUnregister,
	className,
	inputClassName,
	...other
}: UseControllerProps &
	React.HTMLAttributes<HTMLInputElement> & {
		label?: string
		hint?: string
		required?: boolean
		placeholder?: string
		description?: string
		rows?: number
		autoComplete?: string
		autoCorrect?: string
		inputClassName?: string
	}) {
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field: { value, onChange } }) => (
				<FormItem className='w-full'>
					{!!label && (
						<FormLabel>
							{label}
							{hint && (
								<Tooltip>
									<TooltipTrigger asChild>
										<CircleHelpIcon className='flex size-3 items-center text-muted-foreground' />
									</TooltipTrigger>
									<TooltipContent side='top'>{hint}</TooltipContent>
								</Tooltip>
							)}
						</FormLabel>
					)}
					<FormControl className={className}>
						<InlineEditText className={cn('w-full', inputClassName)}>
							<input
								{...other}
								className='-ml-1 block w-full cursor-pointer resize-none overflow-hidden rounded-lg border-transparent bg-transparent p-1 pr-10 leading-6 hover:bg-accent focus:bg-accent focus:outline-hidden'
								value={value}
								onChange={onChange}
							/>
						</InlineEditText>
					</FormControl>
					{!!description && (
						<FormDescription className='items-center'>
							{description}
						</FormDescription>
					)}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
